import { useQuery } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { useState } from 'react';
import type { Billing, PartnerBilling } from '@/api/orgTypes';
import { partnerOptions } from '@/api/partners';
import { billingExportUrl, fetchBilling, fetchPartnerBilling } from '@/api/usage';
import { buttonVariants } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DataTable } from '@/ui/DataTable';
import { PageHeader } from '@/ui/PageHeader';
import { QueryError } from '@/ui/QueryError';

const ALL = 'ALL';
const MONTH_PATTERN = /^\d{4}-(0[1-9]|1[0-2])$/;

function currentMonth(): string {
  return new Date().toISOString().slice(0, 7);
}

type PartnerLine = PartnerBilling['partners'][number];
type OrgLine = Billing['lines'][number];

const partnerColumns: ColumnDef<PartnerLine, never>[] = [
  { accessorKey: 'partner_name', header: 'Partner' },
  { accessorKey: 'targets_published', header: 'Targets published', meta: { numeric: true } },
  { accessorKey: 'posts_published', header: 'Posts published', meta: { numeric: true } },
];

const orgColumns: ColumnDef<OrgLine, never>[] = [
  { accessorKey: 'partner_name', header: 'Partner' },
  { accessorKey: 'org_name', header: 'Organization' },
  { accessorKey: 'targets_published', header: 'Targets published', meta: { numeric: true } },
  { accessorKey: 'posts_published', header: 'Posts published', meta: { numeric: true } },
];

export function UsagePage() {
  const [month, setMonth] = useState(currentMonth);
  const [partnerId, setPartnerId] = useState(ALL);
  const validMonth = MONTH_PATTERN.test(month);
  const partnerFilter = partnerId === ALL ? undefined : partnerId;

  const partners = useQuery({ queryKey: ['partners', 'options'], queryFn: partnerOptions });
  const byPartner = useQuery({
    queryKey: ['usage', 'partners', month],
    queryFn: () => fetchPartnerBilling(month),
    enabled: validMonth,
  });
  const byOrg = useQuery({
    queryKey: ['usage', 'orgs', month, partnerFilter],
    queryFn: () => fetchBilling(month, partnerFilter),
    enabled: validMonth,
  });

  return (
    <>
      <PageHeader
        title="Usage & billing"
        description="Billed quantity is targets published; posts published is shown for reconciling."
        actions={
          validMonth && (
            <a
              className={buttonVariants({ variant: 'outline' })}
              href={billingExportUrl(month, partnerFilter)}
              download
            >
              Download CSV
            </a>
          )
        }
      />

      <div className="flex flex-wrap items-center gap-2">
        <Input
          aria-label="Month"
          type="month"
          className="w-44"
          value={month}
          onChange={(event) => setMonth(event.target.value)}
        />
        <Select value={partnerId} onValueChange={setPartnerId}>
          <SelectTrigger aria-label="Partner" className="w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All partners</SelectItem>
            {partners.data?.map((partner) => (
              <SelectItem key={partner.id} value={partner.id}>
                {partner.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {byPartner.data?.current_month && (
          <span className="text-sm text-muted-foreground">
            This month is still running; figures are as of last night.
          </span>
        )}
      </div>

      {!validMonth ? (
        <p className="text-sm text-destructive">Choose a month.</p>
      ) : (
        <>
          <Card>
            <CardHeader>
              <CardTitle>By partner</CardTitle>
              <CardDescription>One line per partner — the invoice line.</CardDescription>
            </CardHeader>
            <CardContent>
              {byPartner.isError ? (
                <QueryError error={byPartner.error} onRetry={() => void byPartner.refetch()} />
              ) : (
                <>
                  <DataTable
                    columns={partnerColumns}
                    data={byPartner.data?.partners ?? []}
                    getRowId={(row) => row.partner_id}
                    isLoading={byPartner.isPending}
                    empty="Nothing was published this month."
                  />
                  {byPartner.data && (
                    <p className="pt-2 text-right text-sm font-medium">
                      Total: {byPartner.data.totals.targets_published} targets ·{' '}
                      {byPartner.data.totals.posts_published} posts
                    </p>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>By organization</CardTitle>
            </CardHeader>
            <CardContent>
              {byOrg.isError ? (
                <QueryError error={byOrg.error} onRetry={() => void byOrg.refetch()} />
              ) : (
                <>
                  <DataTable
                    columns={orgColumns}
                    data={byOrg.data?.lines ?? []}
                    getRowId={(row) => row.org_id}
                    isLoading={byOrg.isPending}
                    empty="Nothing was published this month."
                  />
                  {byOrg.data && (
                    <p className="pt-2 text-right text-sm font-medium">
                      Total: {byOrg.data.totals.targets_published} targets ·{' '}
                      {byOrg.data.totals.posts_published} posts
                    </p>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </>
  );
}
